import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Loader2, ChevronDown, ChevronUp, Mail, Building, Clock, Tag, User } from 'lucide-react';

interface Lead {
  id: number;
  name: string;
  email: string;
  company: string;
  role: string;
  industry: string;
  revenue: string;
  is_decision_maker: string;
  decision_maker_name: string | null;
  ai_situation: string;
  mandate: string;
  timeline: string;
  referral_source: string | null;
  source: string;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'disqualified', 'converted'];
const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  contacted: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  qualified: 'bg-green-500/10 text-green-400 border-green-500/30',
  disqualified: 'bg-red-500/10 text-red-400 border-red-500/30',
  converted: 'bg-[#E8651A]/10 text-[#E8651A] border-[#E8651A]/30',
};

export const LeadsManager = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await api.get<Lead[]>('/leads');
      setLeads(data || []);
    } catch (err) {
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      setUpdatingStatus(id);
      await api.patch(`/leads/${id}/status`, { status });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filtered = statusFilter === 'all' ? leads : leads.filter(l => l.status === statusFilter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: leads.filter(l => l.status === s).length }), {} as Record<string, number>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8651A]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#f5f5f5] tracking-tight">Leads</h1>
          <p className="text-sm text-[#666] mt-1">{leads.length} total · {counts.new || 0} new</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-300 px-4 py-3 rounded mb-4 text-sm">{error}</div>
      )}

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-1.5 text-xs font-medium border transition-colors ${
            statusFilter === 'all'
              ? 'border-[#E8651A] text-[#E8651A] bg-[#E8651A]/5'
              : 'border-[#222] text-[#888] hover:border-[#444] hover:text-[#f5f5f5]'
          }`}
        >
          All ({leads.length})
        </button>
        {STATUS_OPTIONS.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 text-xs font-medium border transition-colors capitalize ${
              statusFilter === s
                ? 'border-[#E8651A] text-[#E8651A] bg-[#E8651A]/5'
                : 'border-[#222] text-[#888] hover:border-[#444] hover:text-[#f5f5f5]'
            }`}
          >
            {s} ({(counts as any)[s] || 0})
          </button>
        ))}
      </div>

      {/* Leads list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center text-[#555] py-16 bg-[#0a0a0a] border border-[#222] rounded">
            No leads found.
          </div>
        ) : (
          filtered.map(lead => (
            <div key={lead.id} className="bg-[#0a0a0a] border border-[#222] rounded overflow-hidden">
              {/* Summary row */}
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-[#111] transition-colors"
                onClick={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-[#f5f5f5] font-medium truncate">{lead.name}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>
                      {lead.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#555]">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.email}</span>
                    <span className="flex items-center gap-1"><Building className="w-3 h-3" /> {lead.company}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={lead.status}
                    onChange={e => { e.stopPropagation(); updateStatus(lead.id, e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    className="bg-[#111] border border-[#333] rounded px-2 py-1 text-xs text-[#f5f5f5] capitalize"
                    disabled={updatingStatus === lead.id}
                  >
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {expandedId === lead.id ? <ChevronUp className="w-4 h-4 text-[#555]" /> : <ChevronDown className="w-4 h-4 text-[#555]" />}
                </div>
              </div>

              {/* Expanded details */}
              {expandedId === lead.id && (
                <div className="border-t border-[#222] p-4 bg-[#050505]">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Role</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.role}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Industry</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.industry}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Revenue</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.revenue}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Timeline</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.timeline}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Decision Maker</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.is_decision_maker === 'yes' ? 'Yes' : lead.decision_maker_name || 'No'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">AI Situation</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.ai_situation}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Source</p>
                      <p className="text-sm text-[#f5f5f5]">{lead.referral_source || lead.source || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Submitted</p>
                      <p className="text-sm text-[#f5f5f5]">{new Date(lead.created_at).toLocaleString('en-ZA')}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#555] uppercase tracking-wider mb-2">Mandate</p>
                    <p className="text-sm text-[#888] leading-relaxed bg-[#0a0a0a] border border-[#222] rounded p-3">{lead.mandate}</p>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
